"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  InputOptionContext,
  OnboardingData,
  Survey,
  SurveyAnswer,
  SurveyOption,
} from "@/data/onboarding.data";
import { useMobile } from "@/hooks/use-responsive";
import { useOccupationSurvey } from "@/components/pages/onboarding/hooks/use-occupation-survey";
import { useStartingServiceSurvey } from "@/components/pages/onboarding/hooks/use-starting-service-survey";
import { useLoggingContext } from "@/providers/logging-provider/logging-provider";
import { submitSurveyAnswers } from "@/actions/db/survey";
import { useRouter } from "next/navigation";
import { useForcedRedirection } from "@/hooks/providers/forced-redirection-provider";
import { useForcedModalStorage } from "@/hooks/providers/forced-modal-provider";

const OnboardingContext = createContext<OnboardingData | undefined>(undefined);

export const useOnboarding = () => {
  const context = useContext(OnboardingContext);

  if (!context) {
    throw new Error("useOnboarding must be used within a OnboardingProvider");
  }

  return context!;
};

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const logger = useLoggingContext();

  const router = useRouter();

  const { isMobile } = useMobile();

  const [isLoadingSurveys, setIsLoadingSurveys] = useState(false);
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [activeSurveyIndex, setActiveSurveyIndex] = useState<number>(0);

  const [answers, setAnswers] = useState<SurveyAnswer[]>([]);

  const [inputContext, setInputContext] = useState<InputOptionContext>();

  const { isOnboarded, setIsOnboarded } = useForcedRedirection();

  const { setForcedModalValue } = useForcedModalStorage();

  const activeSurvey = useMemo(() => {
    return activeSurveyIndex < surveys.length
      ? surveys[activeSurveyIndex]
      : undefined;
  }, [activeSurveyIndex, surveys]);

  const occupationSurveyProvider = useOccupationSurvey();
  const startingServiceSurveyProvider = useStartingServiceSurvey();

  const fetchSurveys = async () => {
    setIsLoadingSurveys(true);

    try {
      const surveys = await Promise.all([
        occupationSurveyProvider.fetchSurvey(),
        startingServiceSurveyProvider.fetchSurvey(),
      ]);

      setSurveys(surveys);
    } catch (err) {
      await handleFailedOnboarding(err as Error);
    } finally {
      setIsLoadingSurveys(false);
    }
  };

  useEffect(() => {
    resetInputContext();
  }, [activeSurvey]);

  const resetInputContext = () => {
    setInputContext(undefined);
  };

  const handleSelectOption = async (
    option: SurveyOption,
    showInputModalCallback: () => void,
  ) => {
    if (!activeSurvey) {
      throw new Error("Active survey is undefined");
    }

    if (option.answerType === "SELECTION") {
      await handleAnswer({
        type: "SELECTION",
        optionId: option.id,
      });
    } else {
      setInputContext({
        optionId: option.id,
        input: "",
      });

      if (isMobile) {
        showInputModalCallback();
      }
    }
  };

  const handleFailedOnboarding = async (error: Error) => {
    logger.logError(error);
    await finishOnboarding(answers, "/");
  };

  const handleInputSubmit = async () => {
    if (!inputContext) {
      throw new Error("Input context is undefined");
    }

    if (!activeSurvey) {
      throw new Error("Active survey is undefined");
    }

    await handleAnswer({
      type: "INPUT",
      optionId: inputContext.optionId,
      value: inputContext.input,
    });
  };

  const handleAnswer = async (answer: SurveyAnswer) => {
    try {
      const continuationContext =
        await activeSurvey!.handleSubmitAnswer(answer);
      const newAnswers = [...answers, answer];
      setAnswers(newAnswers);

      if (continuationContext.type === "next") {
        setActiveSurveyIndex((prev) => prev + 1);
      } else {
        await finishOnboarding(newAnswers, continuationContext.route);
      }
    } catch (err) {
      await handleFailedOnboarding(err as Error);
    }
  };

  const skipOnboarding = async () => {
    await finishOnboarding(answers, "/");
  };

  const finishOnboarding = async (
    answers: SurveyAnswer[],
    redirectKey: string,
  ) => {
    setForcedModalValue("paywallModalShown", "false");
    setIsOnboarded(true);
    await submitSurveyAnswers(answers);
    router.push(redirectKey);
  };

  const setInput = (value: string) => {
    setInputContext((prev) => ({
      ...prev!,
      input: value,
    }));
  };

  return (
    <OnboardingContext.Provider
      value={{
        isLoadingSurveys,
        activeSurvey,
        inputContext,
        skipOnboarding,
        setInput,
        fetchSurveys,
        handleSelectOption,
        handleInputSubmit,
      }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};
